/**
 * Ein Chat, ein Zustand – der Knopf darf überall stehen.
 *
 * TEAM-CHAT.md nennt als Platz „die Kopfzeile, links neben dem
 * Arbeitsvorrat-Umschalter". Den Umschalter gibt es nur auf „Mein Tag";
 * die übrigen Masken haben eigene Kopfzeilen. Deshalb hält die Shell den
 * Zustand und das Panel, und der Knopf ist ein Bauteil, das jede Maske an
 * ihre eigene Kopfzeile hängen kann.
 *
 * Damit er nicht doppelt erscheint, meldet sich jeder gesetzte Knopf an.
 * Die Shell zeigt ihren eigenen nur, solange keine Maske einen gesetzt hat –
 * so steht er überall, aber nie zweimal.
 */

import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api';
import { ChatPanel } from './ChatPanel';
import type { ChatUeberblick } from '../typen';

interface Kontext {
  offen: boolean;
  umschalten: () => void;
  ungelesen: number;
}

const ChatKontext = createContext<Kontext | null>(null);

export function ChatVersorgung({ children }: { children: ReactNode }) {
  const [offen, setOffen] = useState(false);

  const ueberblick = useQuery({
    queryKey: ['chat-ueberblick', ''],
    queryFn: () => api.hole<ChatUeberblick>('/chat/ueberblick'),
    /*
     * Alle 20 Sekunden. Ohne Dauerverbindung ist das der Kompromiss: schnell
     * genug, dass niemand eine Nachricht verpasst, selten genug, dass fünf
     * offene Browser den Server nicht beschäftigen.
     */
    refetchInterval: 20_000,
    retry: false,
  });

  const wert = useMemo<Kontext>(
    () => ({
      offen,
      umschalten: () => setOffen((o) => !o),
      ungelesen: ueberblick.data?.gesamt ?? 0,
    }),
    [offen, ueberblick.data?.gesamt],
  );

  return (
    <ChatKontext.Provider value={wert}>
      {children}
      {offen && <ChatPanel onSchliessen={() => setOffen(false)} />}
    </ChatKontext.Provider>
  );
}

export function useChat(): Kontext {
  const k = useContext(ChatKontext);
  if (!k) throw new Error('useChat braucht die ChatVersorgung.');
  return k;
}

/**
 * Das Nachrichten-Symbol.
 *
 * Ohne Ungelesenes ein ruhiger Umrissknopf. Mit Ungelesenem wächst er zu
 * einer gelben Fläche mit der Zahl daneben: Ein kleines Abzeichen in der
 * Ecke übersieht man in einer Maske voller Zahlen, und genau das darf bei
 * einer Nachricht nicht passieren.
 */
export function Nachrichtenknopf() {
  const { umschalten, ungelesen } = useChat();

  const titel =
    ungelesen === 0
      ? 'Team-Chat öffnen'
      : `${ungelesen} ungelesen${ungelesen === 1 ? 'e Nachricht' : 'e Nachrichten'} – Team, Direkt und Kommentare`;

  return (
    <button
      type="button"
      className="ch-symbol"
      title={titel}
      aria-label={titel}
      onClick={umschalten}
    >
      <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 11.5a8.4 8.4 0 0 1-9 8.4 8.6 8.6 0 0 1-3.8-.9L3 21l1.9-5A8.4 8.4 0 1 1 21 11.5z" />
      </svg>
      {ungelesen > 0 && <span className="ch-symbol-zahl">{ungelesen > 99 ? '99+' : ungelesen}</span>}
    </button>
  );
}
